import { useQuery } from "@tanstack/react-query";
import {
  Cpu,
  KeyRound,
  Shuffle,
  Sparkles,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import {
  fetchConfig,
  fetchLicenseStatus,
  fetchTranslators,
  type ConfigResponse,
  type LicenseStatus,
  type TranslatorDescriptor,
} from "@/lib/api";

/**
 * Settings page — read-only runtime introspection.
 *
 * <p>Sections:
 * <ul>
 *   <li><strong>Runtime</strong> — LLM provider (relevant once Wave-2
 *       LLM-assisted translation lands; "fallback" today is normal),
 *       product version, registered translator count.</li>
 *   <li><strong>License</strong> — full status pulled from
 *       {@code /api/v1/license/status}, mirrored next to the header
 *       badge so the operator sees the hardware fingerprint at a
 *       glance.</li>
 *   <li><strong>Feature flags</strong> — per-feature on/off so an
 *       operator can diagnose "why is Browse Cognos greyed out"
 *       without shelling into the container.</li>
 *   <li><strong>Translators</strong> — registry of every loaded
 *       {@code Translator} bean with the {@code TargetPlatform}s it
 *       advertises support for. The Translate page's target picker
 *       is built from the union of these.</li>
 * </ul>
 */
export function SettingsPage() {
  const config = useQuery({
    queryKey: ["bridge-config"],
    queryFn: fetchConfig,
    staleTime: 60_000,
    retry: false,
  });
  const license = useQuery({
    queryKey: ["license-status"],
    queryFn: fetchLicenseStatus,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const translators = useQuery({
    queryKey: ["bridge-translators"],
    queryFn: fetchTranslators,
    staleTime: 60_000,
    retry: false,
  });

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <h1 className="font-display text-2xl font-semibold text-bridge-ink">Settings</h1>
      <p className="text-sm text-bridge-slate">
        Read-only summary of how Bridge is running on this host. Use
        this to confirm a deployment is wired correctly before running
        a translation.
      </p>

      <div data-tour-id="settings-llm">
      <Section icon={<Cpu size={14} />} title="Runtime">
        <Row label="Active LLM provider" value={config.data?.llmProvider ?? "—"} />
        <Row label="Product version" value={config.data?.productVersion ?? "—"} mono />
        <Row
          label="Registered translators"
          value={config.data?.translatorCount?.toString() ?? "—"}
        />
        {config.data?.llmProvider === "fallback" && (
          <p className="mt-2 rounded-md bg-bridge-mist px-3 py-2 text-2xs text-bridge-slate">
            Bridge Wave 1 is rule-based — no LLM is required, so the
            "fallback" provider is the normal deployment shape. Set
            <code className="ml-1">CLOUDCRAFT_LLM_PROVIDER</code> in your{" "}
            <code>.env</code> to enable Wave-2 LLM-assisted
            ambiguous-case translation when it ships.
          </p>
        )}
      </Section>
      </div>

      <Section icon={<KeyRound size={14} />} title="License">
        {license.isError && (
          <p className="text-sm text-bridge-slate">
            License gate disabled on this deployment.
          </p>
        )}
        {license.data && <LicenseRows status={license.data} />}
      </Section>

      <Section icon={<Sparkles size={14} />} title="Feature flags">
        {config.data?.featureFlags ? (
          <ul className="space-y-1.5">
            {Object.entries(config.data.featureFlags).map(([flag, on]) => (
              <li key={flag} className="flex items-center justify-between text-sm">
                <span className="text-bridge-ink">{flag}</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-medium ${
                    on
                      ? "bg-bridge-success/10 text-bridge-success"
                      : "bg-bridge-slate/10 text-bridge-slate"
                  }`}
                >
                  {on ? <ToggleRight size={11} /> : <ToggleLeft size={11} />}
                  {on ? "on" : "off"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-bridge-slate">Loading feature flags…</p>
        )}
      </Section>

      <Section icon={<Shuffle size={14} />} title="Translators">
        {translators.isLoading && (
          <p className="text-sm text-bridge-slate">Loading translators…</p>
        )}
        {translators.error && (
          <p className="text-sm text-bridge-danger">
            Couldn't load translators — {(translators.error as Error).message}
          </p>
        )}
        {translators.data && translators.data.length === 0 && (
          <p className="text-sm text-bridge-slate">No translators registered.</p>
        )}
        {translators.data && translators.data.length > 0 && (
          <ul className="space-y-1.5">
            {translators.data.map((t) => (
              <TranslatorRow key={t.id} translator={t} />
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function TranslatorRow({ translator }: { translator: TranslatorDescriptor }) {
  return (
    <li className="rounded-md bg-bridge-mist/40 px-3 py-2 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-mono text-bridge-ink">{translator.id}</span>
        <span className="text-2xs text-bridge-slate">{translator.className}</span>
      </div>
      {translator.supportedTargets.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {translator.supportedTargets.map((t) => (
            <span
              key={t}
              className="inline-flex items-center rounded-full bg-bridge-primary/10 px-2 py-0.5 text-2xs font-medium text-bridge-primary"
            >
              {t}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

function LicenseRows({ status }: { status: LicenseStatus }) {
  return (
    <>
      <Row
        label="Status"
        value={status.ok ? "Valid" : `Invalid: ${status.failureReason ?? "unknown"}`}
        tone={status.ok ? "ok" : "bad"}
      />
      {status.ok && (
        <>
          <Row label="Issued to" value={status.customerName ?? "—"} />
          <Row
            label="Expires"
            value={`${formatDate(status.expiresAt)} (${status.daysRemaining}d)`}
            tone={status.daysRemaining <= 14 ? "warn" : undefined}
          />
          <Row label="Issued" value={formatDate(status.issuedAt)} />
        </>
      )}
      {!status.ok && status.failureMessage && (
        <p className="mt-2 rounded-md bg-bridge-danger/10 px-3 py-2 text-2xs text-bridge-danger">
          {status.failureMessage}
        </p>
      )}
      <Row label="Hardware fingerprint" value={status.fingerprint} mono />
    </>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-bridge-slate/20 bg-white p-4 shadow-card">
      <h2 className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-bridge-ink">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "ok" | "warn" | "bad";
}) {
  const valueClass =
    tone === "ok"
      ? "text-bridge-success"
      : tone === "warn"
      ? "text-bridge-warn"
      : tone === "bad"
      ? "text-bridge-danger"
      : "text-bridge-ink";
  return (
    <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 py-1 text-sm">
      <dt className="text-bridge-slate">{label}</dt>
      <dd
        className={`text-right font-medium ${valueClass} ${
          mono ? "break-all font-mono text-2xs" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
